import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { environment } from '../../../environments/environment';

/**
 * Where a check-in or check-out happened.
 * - `Office`     — inside the configured office geofence.
 * - `Remote`     — outside it.
 * - `Unverified` — position known, but no office is configured to compare it against.
 * - `Not shared` — the employee denied the permission, or the device could not get a fix.
 */
export type AttendancePlace = 'Office' | 'Remote' | 'Unverified' | 'Not shared';

export interface LocationVerdict {
  place: AttendancePlace;
  /** Radius the device reported for its own fix. Omitted when no fix was obtained. */
  accuracyMeters?: number;
}

/**
 * Answers one question about a check-in — "were they in the building?" — and nothing more.
 *
 * The browser's coordinates are read, compared against the office geofence, and then thrown
 * away: only the verdict ("Office"/"Remote") is ever returned, so no employee's raw position
 * is stored or transmitted. This is deliberate. Storing precise location for every employee
 * every day is personal data under GDPR and buys nothing a geofence verdict does not.
 *
 * Never fails: a denied permission, a missing GPS fix or an insecure origin all resolve to
 * "Not shared" so attendance can still be recorded. Losing someone's whole working day
 * because their browser blocked a permission prompt would be the worse failure.
 */
@Injectable({ providedIn: 'root' })
export class AttendanceLocationService {
  /** Give up on a slow fix rather than make the user wait to check in. */
  private readonly timeoutMs = 10_000;

  private get office() {
    return environment.attendance?.office ?? null;
  }

  capture(): Observable<LocationVerdict> {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return of({ place: 'Not shared' as const });
    }

    return new Observable<LocationVerdict>(subscriber => {
      navigator.geolocation.getCurrentPosition(
        position => {
          subscriber.next(this.classify(position));
          subscriber.complete();
        },
        () => {
          // Denied, unavailable, timed out, or a non-HTTPS origin — all the same to us.
          subscriber.next({ place: 'Not shared' });
          subscriber.complete();
        },
        {
          // Street-level accuracy is plenty to tell "in the office" from "at home", and it is
          // far faster and cheaper on battery than a high-accuracy GPS lock.
          enableHighAccuracy: false,
          timeout: this.timeoutMs,
          maximumAge: 60_000,
        },
      );
    });
  }

  /** Human label for a verdict, e.g. "Office (±18m)". */
  label(verdict: LocationVerdict): string {
    if (verdict.place === 'Not shared' || verdict.accuracyMeters == null) return verdict.place;
    return `${verdict.place} (±${Math.round(verdict.accuracyMeters)}m)`;
  }

  private classify(position: GeolocationPosition): LocationVerdict {
    const accuracyMeters = position.coords.accuracy;
    const office = this.office;
    if (!office) return { place: 'Unverified', accuracyMeters };

    const distance = this.distanceMeters(
      position.coords.latitude, position.coords.longitude,
      office.lat, office.lng,
    );
    // Allow the device's own margin of error, so a fix that is merely imprecise does not
    // get someone marked Remote while they are sitting at their desk.
    const inside = distance <= office.radiusMeters + accuracyMeters;
    return { place: inside ? 'Office' : 'Remote', accuracyMeters };
  }

  /** Great-circle distance in metres (haversine). */
  private distanceMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const earthRadius = 6_371_000;
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2
      + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 2 * earthRadius * Math.asin(Math.sqrt(a));
  }
}
